import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useTheme } from "@/App";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Users, CheckCircle, X, Search, Filter, ArrowRightLeft,
  Loader2, RefreshCw, Layers, Calendar, Clock, AlertCircle,
  Check, Sparkles, User, Mail, ChevronRight, Shield
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "@/components/ui/dialog";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true
    });
  } catch { return iso; }
};

export default function BatchEnrollmentsAdminTab() {
  const { theme } = useTheme();
  const isLight = theme === "light";

  const [enrollments, setEnrollments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [batchFilter, setBatchFilter] = useState("all");
  const [actionLoading, setActionLoading] = useState(null);

  // Switch Batch Modal state
  const [switchTarget, setSwitchTarget] = useState(null); // enrollment object
  const [selectedNewBatchId, setSelectedNewBatchId] = useState("");
  const [switching, setSwitching] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [enrollRes, batchRes] = await Promise.all([
        axios.get(`${API}/admin/batch-enrollments`, { withCredentials: true }),
        axios.get(`${API}/batches`, { withCredentials: true })
      ]);
      setEnrollments(enrollRes.data || []);
      setBatches(batchRes.data || []);
    } catch (err) {
      console.error("Error fetching batch enrollments:", err);
      toast.error("Failed to load batch enrollments");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle status update (Approve / Reject / Revoke)
  const handleStatusChange = async (enrollment, newStatus) => {
    setActionLoading(enrollment.enrollment_id);
    try {
      await axios.put(
        `${API}/admin/batch-enrollments/${enrollment.enrollment_id}`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success(
        newStatus === "approved"
          ? `Approved enrollment for ${enrollment.user_name || "student"}`
          : `Marked enrollment as ${newStatus}`
      );
      setEnrollments(prev =>
        prev.map(item =>
          item.enrollment_id === enrollment.enrollment_id
            ? { ...item, status: newStatus }
            : item
        )
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update enrollment status");
    } finally {
      setActionLoading(null);
    }
  };

  // Open switch batch modal
  const openSwitchModal = (enrollment) => {
    setSwitchTarget(enrollment);
    const otherBatches = batches.filter(b => b.batch_id !== enrollment.batch_id);
    setSelectedNewBatchId(otherBatches[0]?.batch_id || "");
  };

  // Confirm batch switch
  const handleConfirmSwitch = async () => {
    if (!switchTarget || !selectedNewBatchId) return;
    if (selectedNewBatchId === switchTarget.batch_id) {
      toast.error("Please select a different batch to switch to");
      return;
    }
    setSwitching(true);
    try {
      const res = await axios.post(
        `${API}/admin/batch-enrollments/${switchTarget.enrollment_id}/switch-batch`,
        { new_batch_id: selectedNewBatchId },
        { withCredentials: true }
      );
      toast.success(res.data.message || "Student shifted to new batch successfully!");
      setEnrollments(prev =>
        prev.map(item =>
          item.enrollment_id === switchTarget.enrollment_id
            ? {
                ...item,
                batch_id: selectedNewBatchId,
                batch_name: res.data.new_batch_name,
                previous_batch_name: switchTarget.batch_name
              }
            : item
        )
      );
      setSwitchTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to switch batch");
    } finally {
      setSwitching(false);
    }
  };

  // Filtered list
  const filtered = enrollments.filter(e => {
    if (statusFilter !== "all" && e.status !== statusFilter) return false;
    if (batchFilter !== "all" && e.batch_id !== batchFilter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      const matchName = (e.user_name || "").toLowerCase().includes(q);
      const matchEmail = (e.user_email || "").toLowerCase().includes(q);
      const matchBatch = (e.batch_name || "").toLowerCase().includes(q);
      if (!matchName && !matchEmail && !matchBatch) return false;
    }
    return true;
  });

  const pendingCount = enrollments.filter(e => e.status === "pending").length;
  const approvedCount = enrollments.filter(e => e.status === "approved").length;
  const rejectedCount = enrollments.filter(e => e.status === "rejected").length;

  return (
    <div className="space-y-6">
      {/* Top Banner & Stats with rich glassmorphism */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        {/* Total Requests */}
        <div className={`p-4 sm:p-5 rounded-2xl border transition-all ${
          isLight
            ? "bg-white border-slate-200/90 shadow-sm hover:shadow-md"
            : "bg-[#111622] border-white/[0.08] shadow-lg hover:border-white/15"
        }`}>
          <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wider mb-2 text-slate-500 dark:text-zinc-400">
            <span>Total Requests</span>
            <div className="w-8 h-8 rounded-xl bg-violet-500/10 text-violet-600 dark:text-violet-400 flex items-center justify-center font-bold">
              <Users className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black font-outfit text-slate-900 dark:text-white">
            {enrollments.length}
          </div>
        </div>

        {/* Pending Review */}
        <div className={`p-4 sm:p-5 rounded-2xl border transition-all ${
          isLight
            ? "bg-amber-50/70 border-amber-300 shadow-sm hover:shadow-md"
            : "bg-amber-500/[0.07] border-amber-500/30 shadow-lg hover:border-amber-500/50"
        }`}>
          <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wider mb-2 text-amber-700 dark:text-amber-400">
            <span>Pending Review</span>
            <div className="w-8 h-8 rounded-xl bg-amber-500/20 text-amber-600 dark:text-amber-400 flex items-center justify-center">
              <AlertCircle className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black font-outfit text-amber-700 dark:text-amber-400">
            {pendingCount}
          </div>
        </div>

        {/* Approved Students */}
        <div className={`p-4 sm:p-5 rounded-2xl border transition-all ${
          isLight
            ? "bg-emerald-50/70 border-emerald-300 shadow-sm hover:shadow-md"
            : "bg-emerald-500/[0.07] border-emerald-500/30 shadow-lg hover:border-emerald-500/50"
        }`}>
          <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wider mb-2 text-emerald-700 dark:text-emerald-400">
            <span>Approved Students</span>
            <div className="w-8 h-8 rounded-xl bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
              <CheckCircle className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black font-outfit text-emerald-700 dark:text-emerald-400">
            {approvedCount}
          </div>
        </div>

        {/* Active Batches */}
        <div className={`p-4 sm:p-5 rounded-2xl border transition-all ${
          isLight
            ? "bg-white border-slate-200/90 shadow-sm hover:shadow-md"
            : "bg-[#111622] border-white/[0.08] shadow-lg hover:border-white/15"
        }`}>
          <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wider mb-2 text-slate-500 dark:text-zinc-400">
            <span>Active Batches</span>
            <div className="w-8 h-8 rounded-xl bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-400 flex items-center justify-center">
              <Layers className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black font-outfit text-slate-900 dark:text-white">
            {batches.length}
          </div>
        </div>
      </div>

      {/* Filter and Search Bar with High Contrast */}
      <div className={`p-4 sm:p-5 rounded-2xl border flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 ${
        isLight ? "bg-white border-slate-200 shadow-sm" : "bg-[#111622] border-white/[0.08]"
      }`}>
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3.5 top-3 w-4 h-4 text-slate-400 dark:text-zinc-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search student name, email, or batch..."
            className={`pl-10 h-10 text-xs sm:text-sm font-medium rounded-xl border ${
              isLight
                ? "bg-slate-50 border-slate-300 text-slate-900 placeholder-slate-400 focus:bg-white focus:border-violet-500"
                : "bg-black/40 border-white/10 text-white placeholder-zinc-500 focus:border-violet-500"
            }`}
          />
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          {/* Batch Filter dropdown */}
          <select
            value={batchFilter}
            onChange={(e) => setBatchFilter(e.target.value)}
            className={`h-10 px-3.5 rounded-xl text-xs font-bold border outline-none transition-all cursor-pointer ${
              isLight
                ? "bg-white border-slate-300 text-slate-800 hover:border-slate-400 focus:ring-2 focus:ring-violet-500"
                : "bg-[#182030] border-white/15 text-zinc-200 hover:border-white/30 focus:ring-2 focus:ring-violet-500"
            }`}
          >
            <option value="all">All Batches ({batches.length})</option>
            {batches.map(b => (
              <option key={b.batch_id} value={b.batch_id}>{b.name}</option>
            ))}
          </select>

          {/* Status Filter buttons */}
          <div className={`flex rounded-xl p-1 border text-xs ${
            isLight ? "bg-slate-100 border-slate-200" : "bg-black/30 border-white/10"
          }`}>
            {[
              { id: "all", label: "All" },
              { id: "pending", label: `Pending (${pendingCount})` },
              { id: "approved", label: "Approved" },
              { id: "rejected", label: "Rejected" },
            ].map(st => (
              <button
                key={st.id}
                onClick={() => setStatusFilter(st.id)}
                className={`px-3 py-1.5 rounded-lg font-bold transition-all ${
                  statusFilter === st.id
                    ? isLight
                      ? "bg-white text-slate-900 shadow-sm border border-slate-200"
                      : "bg-white/15 text-white shadow-sm"
                    : isLight
                    ? "text-slate-600 hover:text-slate-900"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                {st.label}
              </button>
            ))}
          </div>

          <Button
            size="sm"
            variant="outline"
            onClick={fetchData}
            className={`h-10 px-3 rounded-xl border ${
              isLight
                ? "border-slate-300 text-slate-800 hover:bg-slate-100"
                : "border-white/15 text-zinc-200 hover:bg-white/10"
            }`}
            title="Refresh Enrollments"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin text-violet-500" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Enrollments Table / List */}
      <div className={`rounded-2xl border overflow-hidden transition-all ${
        isLight ? "bg-white border-slate-200 shadow-sm" : "bg-[#111622] border-white/[0.08]"
      }`}>
        {loading ? (
          <div className="py-24 flex flex-col items-center justify-center gap-3">
            <Loader2 className="w-10 h-10 animate-spin text-violet-500" />
            <p className="text-xs font-semibold text-slate-500 dark:text-zinc-400">Loading student batch enrollments...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center">
            <div className="w-16 h-16 rounded-2xl bg-violet-500/10 text-violet-500 flex items-center justify-center mx-auto mb-3">
              <Users className="w-8 h-8 opacity-60" />
            </div>
            <p className="font-bold text-base text-slate-900 dark:text-white">No batch enrollments found</p>
            <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1 max-w-sm mx-auto">
              No student enrollment requests match the current search or filters. Try clearing your search keywords.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className={`border-b font-bold uppercase tracking-wider text-[11px] ${
                  isLight
                    ? "bg-slate-50/90 text-slate-700 border-slate-200"
                    : "bg-white/[0.03] text-zinc-400 border-white/[0.08]"
                }`}>
                  <th className="py-3.5 px-5">Student</th>
                  <th className="py-3.5 px-5">Assigned Batch</th>
                  <th className="py-3.5 px-5">Requested Date</th>
                  <th className="py-3.5 px-5">Status</th>
                  <th className="py-3.5 px-5 text-right">Batch Management & Actions</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isLight ? "divide-slate-100" : "divide-white/[0.05]"}`}>
                {filtered.map(enrollment => {
                  const isCurrentAction = actionLoading === enrollment.enrollment_id;
                  return (
                    <tr
                      key={enrollment.enrollment_id}
                      className={`transition-colors ${
                        isLight ? "hover:bg-slate-50/80" : "hover:bg-white/[0.02]"
                      }`}
                    >
                      {/* Student info */}
                      <td className="py-4 px-5">
                        <div className="font-bold text-slate-900 dark:text-white text-sm">
                          {enrollment.user_name || "Unknown Student"}
                        </div>
                        <div className="text-slate-500 dark:text-zinc-400 text-xs mt-0.5 font-medium">
                          {enrollment.user_email}
                        </div>
                      </td>

                      {/* Current Batch */}
                      <td className="py-4 px-5">
                        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 border border-violet-500/30 text-violet-700 dark:text-violet-300 font-bold text-xs">
                          <Layers className="w-3.5 h-3.5 text-violet-500 shrink-0" />
                          <span>{enrollment.batch_name || "Unknown Batch"}</span>
                        </div>
                        {enrollment.previous_batch_name && (
                          <div className="text-[10px] text-slate-500 dark:text-zinc-400 mt-1 flex items-center gap-1 font-semibold">
                            <span>Previously: {enrollment.previous_batch_name}</span>
                          </div>
                        )}
                      </td>

                      {/* Requested At */}
                      <td className="py-4 px-5 text-slate-600 dark:text-zinc-300 font-medium whitespace-nowrap">
                        {fmtDate(enrollment.requested_at)}
                      </td>

                      {/* Status */}
                      <td className="py-4 px-5">
                        {enrollment.status === "approved" && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-black px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30">
                            <CheckCircle className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                            Approved
                          </span>
                        )}
                        {enrollment.status === "pending" && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-black px-3 py-1 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30">
                            <Clock className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                            Pending Review
                          </span>
                        )}
                        {enrollment.status === "rejected" && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-black px-3 py-1 rounded-full bg-red-500/15 text-red-700 dark:text-red-400 border border-red-500/30">
                            <X className="w-3.5 h-3.5 text-red-600 dark:text-red-400" />
                            Rejected
                          </span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="py-4 px-5 text-right">
                        <div className="flex items-center justify-end gap-2 flex-wrap">
                          {/* Switch Batch Button */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openSwitchModal(enrollment)}
                            className={`h-8 text-xs font-bold rounded-xl border transition-all ${
                              isLight
                                ? "border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100 hover:border-violet-400"
                                : "border-violet-500/30 bg-violet-500/10 text-violet-300 hover:bg-violet-500/20"
                            }`}
                          >
                            <ArrowRightLeft className="w-3.5 h-3.5 mr-1 text-violet-500" />
                            Switch Batch
                          </Button>

                          {/* Quick Status controls */}
                          {enrollment.status === "pending" && (
                            <>
                              <Button
                                size="sm"
                                disabled={isCurrentAction}
                                onClick={() => handleStatusChange(enrollment, "approved")}
                                className="h-8 text-xs bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold px-3 shadow-sm"
                              >
                                {isCurrentAction ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Approve"}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={isCurrentAction}
                                onClick={() => handleStatusChange(enrollment, "rejected")}
                                className={`h-8 text-xs rounded-xl font-bold px-3 border ${
                                  isLight
                                    ? "border-red-200 text-red-600 hover:bg-red-50"
                                    : "border-red-500/30 text-red-400 hover:bg-red-500/10"
                                }`}
                              >
                                Reject
                              </Button>
                            </>
                          )}

                          {enrollment.status === "approved" && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={isCurrentAction}
                              onClick={() => handleStatusChange(enrollment, "rejected")}
                              className={`h-8 text-xs rounded-xl font-bold px-3 border ${
                                isLight
                                  ? "border-slate-300 text-slate-700 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                                  : "border-white/10 text-zinc-300 hover:bg-red-500/10 hover:text-red-400"
                              }`}
                            >
                              Revoke
                            </Button>
                          )}

                          {enrollment.status === "rejected" && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={isCurrentAction}
                              onClick={() => handleStatusChange(enrollment, "approved")}
                              className={`h-8 text-xs rounded-xl font-bold px-3 border ${
                                isLight
                                  ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                  : "border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                              }`}
                            >
                              Re-approve
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* HIGH-END SWITCH BATCH MODAL (NO BASIC NATIVE SELECT!)             */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <Dialog open={!!switchTarget} onOpenChange={(open) => !open && setSwitchTarget(null)}>
        <DialogContent className={`max-w-lg w-full p-6 rounded-3xl border shadow-2xl ${
          isLight ? "bg-white border-slate-200" : "bg-[#0F141F] border-white/15"
        }`}>
          <DialogHeader className="mb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white flex items-center justify-center shadow-lg">
                <ArrowRightLeft className="w-5 h-5" />
              </div>
              <div>
                <DialogTitle className={`font-outfit text-xl font-black ${
                  isLight ? "text-slate-900" : "text-white"
                }`}>
                  Move Student to Another Batch
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-500 dark:text-zinc-400">
                  Select the destination batch to transfer this student.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {switchTarget && (
            <div className="space-y-4 py-2">
              {/* Current Student & Current Batch Pill */}
              <div className={`p-4 rounded-2xl border flex items-center justify-between gap-3 ${
                isLight ? "bg-slate-50 border-slate-200" : "bg-black/30 border-white/[0.08]"
              }`}>
                <div className="min-w-0">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-400">
                    Selected Student
                  </div>
                  <div className="font-black text-sm text-slate-900 dark:text-white truncate">
                    {switchTarget.user_name || "Student"}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-zinc-400 truncate">
                    {switchTarget.user_email}
                  </div>
                </div>

                <div className="text-right shrink-0">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-400 mb-0.5">
                    Current Batch
                  </div>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-violet-500/15 border border-violet-500/30 text-violet-700 dark:text-violet-300 font-black text-xs">
                    <Layers className="w-3.5 h-3.5 text-violet-500" />
                    {switchTarget.batch_name}
                  </span>
                </div>
              </div>

              {/* Destination Batch Selector (Cards instead of OS Select) */}
              <div>
                <label className={`text-xs font-bold uppercase tracking-wider block mb-2 ${
                  isLight ? "text-slate-700" : "text-zinc-300"
                }`}>
                  Choose Destination Batch:
                </label>

                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {batches.map(b => {
                    const isCurrent = b.batch_id === switchTarget.batch_id;
                    const isSelected = selectedNewBatchId === b.batch_id;

                    return (
                      <div
                        key={b.batch_id}
                        onClick={() => {
                          if (!isCurrent) setSelectedNewBatchId(b.batch_id);
                        }}
                        className={`p-3.5 rounded-2xl border transition-all flex items-center justify-between gap-3 ${
                          isCurrent
                            ? isLight
                              ? "bg-slate-100/70 border-slate-200 opacity-60 cursor-not-allowed"
                              : "bg-white/[0.02] border-white/5 opacity-40 cursor-not-allowed"
                            : isSelected
                            ? isLight
                              ? "bg-violet-50 border-violet-500 ring-2 ring-violet-500/20 shadow-sm cursor-pointer"
                              : "bg-violet-600/15 border-violet-500 ring-2 ring-violet-500/30 shadow-lg cursor-pointer"
                            : isLight
                            ? "bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50 cursor-pointer"
                            : "bg-[#182030] border-white/[0.08] hover:border-white/20 hover:bg-white/[0.04] cursor-pointer"
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          {/* Radio circle indicator */}
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center border transition-all ${
                            isSelected
                              ? "bg-violet-600 border-violet-600 text-white"
                              : isLight
                              ? "border-slate-300 bg-white"
                              : "border-white/20 bg-black/40"
                          }`}>
                            {isSelected && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                          </div>

                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`font-black text-sm truncate ${
                                isSelected
                                  ? isLight ? "text-violet-900" : "text-white"
                                  : isLight ? "text-slate-900" : "text-zinc-200"
                              }`}>
                                {b.name}
                              </span>
                              {isCurrent && (
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30">
                                  Current Batch
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-slate-500 dark:text-zinc-400 mt-0.5 flex items-center gap-3">
                              <span>{b.enrolled_count || 0} students enrolled</span>
                              {b.start_date && (
                                <span>Starts: {new Date(b.start_date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {isSelected && (
                          <span className="text-xs font-bold text-violet-600 dark:text-violet-400 flex items-center gap-0.5 shrink-0">
                            Selected
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>

                <p className="text-[11px] text-slate-500 dark:text-zinc-400 mt-2">
                  All live Zoom class schedules and recorded lessons will transition to the selected batch immediately.
                </p>
              </div>

              {/* Action Buttons with high contrast */}
              <div className="flex gap-2.5 justify-end pt-3 border-t border-slate-100 dark:border-white/[0.08]">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSwitchTarget(null)}
                  disabled={switching}
                  className={`h-10 px-4 rounded-xl font-bold border ${
                    isLight
                      ? "border-slate-300 text-slate-700 hover:bg-slate-100"
                      : "border-white/15 text-zinc-300 hover:bg-white/10"
                  }`}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={switching || !selectedNewBatchId || selectedNewBatchId === switchTarget.batch_id}
                  onClick={handleConfirmSwitch}
                  className="h-10 px-5 bg-gradient-to-r from-violet-600 via-fuchsia-600 to-pink-600 text-white rounded-xl font-black shadow-lg hover:opacity-95 disabled:opacity-50"
                >
                  {switching ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                      Switching Batch...
                    </>
                  ) : (
                    <>
                      <ArrowRightLeft className="w-4 h-4 mr-1.5" />
                      Confirm Transfer
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
